/* eslint-disable no-use-before-define */
// Box walking is inherently sequential — each box's size tells us where the
// next one starts — so the awaits below cannot be parallelised.
/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
/**
 * Client-side video duration reader for MMS payloads.
 *
 * Shared by every send path (activity center, browser-extension-v4, …) via
 * `handleFiles`. Import `buildVideoDurationField` rather than reimplementing —
 * the whole point of this module is that all clients agree on the value.
 *
 * WHY NOT JUST USE AN HTMLVideoElement?
 * That is the common approach, but it requires the browser to DECODE the file,
 * and it fails silently for two very common real-world inputs:
 *   - iPhone HEVC/H.265 `.mov` — no desktop browser decodes it
 *   - any H.264 file on a Linux Chromium built without proprietary codecs
 * In both cases the element fires `error`, duration comes back null, and the
 * field gets dropped from the MMS with no visible symptom. That is the exact
 * defect this module fixes.
 *
 * STRATEGY (first success wins):
 *   1. Parse the MP4/MOV container directly — codec-independent, reads only a
 *      few hundred bytes, works for HEVC and on codec-less browsers.
 *   2. Fall back to an HTMLVideoElement for containers we don't parse
 *      (WebM/Ogg), which browsers decode natively anyway.
 */

// --- ISO-BMFF (MP4 / MOV / M4V / 3GP) box parsing -------------------------

const HEADER_BYTES = 8; // size (uint32) + type (4 chars)
// mvhd is normally moov's first child; 512B covers its header plus a
// 64-bit (version 1) body without reading the whole moov box.
const MVHD_SCAN_BYTES = 512;
// Forward walk handles "faststart" files where moov sits near the front.
// Kept small on purpose: camera-recorded files can have THOUSANDS of top-level
// mdat boxes (a real 110MB test file had moov as box #3113), and walking those
// one slice at a time is far slower than the tail scan below.
const FORWARD_WALK_LIMIT = 32;
// Recorded video puts moov at the END, so scan backwards from the tail in
// expanding windows. 1MB catches the overwhelming majority.
const MB = 1024 * 1024;
const TAIL_SCAN_WINDOWS = [1 * MB, 8 * MB, 64 * MB];
// False "moov" byte sequences inside media data are possible; try a few then
// give up rather than rescanning the window indefinitely.
const MAX_TAIL_CANDIDATES = 8;
const VIDEO_ELEMENT_TIMEOUT_MS = 5000;
// Absolute ceiling for the whole read, covering both strategies. Exists so a
// FileReader that never fires onload/onerror can't leave a send hanging: the
// duration is optional, the MMS is not.
const OVERALL_TIMEOUT_MS = 15000;

const VIDEO_EXTENSIONS = ["mp4", "mov", "m4v", "3gp", "3g2", "avi", "webm", "mkv", "mpeg", "mpg"];

const readSlice = (file, start, end) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file.slice(start, end));
  });

const u32 = (bytes, offset) =>
  bytes[offset] * 2 ** 24 +
  bytes[offset + 1] * 2 ** 16 +
  bytes[offset + 2] * 256 +
  bytes[offset + 3];

const typeAt = (bytes, offset) =>
  String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3]);

/**
 * Extracts (duration / timescale) from the mvhd box inside a moov body.
 * Handles version 0 (32-bit) and version 1 (64-bit) field layouts.
 *
 * @param {Uint8Array} moovBody Bytes starting at moov's first child box.
 * @returns {number|null} seconds, or null when mvhd isn't found/parsable.
 */
const readDurationFromMoovBody = moovBody => {
  let offset = 0;
  while (offset + HEADER_BYTES <= moovBody.length) {
    const boxSize = u32(moovBody, offset);
    if (typeAt(moovBody, offset + 4) === "mvhd") {
      const body = offset + HEADER_BYTES;
      const version = moovBody[body];
      const fieldSize = version === 1 ? 8 : 4;
      // version(1) + flags(3) + creationTime + modificationTime
      const timescaleAt = body + 4 + fieldSize * 2;
      if (timescaleAt + 4 + fieldSize > moovBody.length) return null;
      const timescale = u32(moovBody, timescaleAt);
      // v1 duration is 64-bit; compose both words instead of truncating.
      const duration =
        version === 1
          ? u32(moovBody, timescaleAt + 4) * 2 ** 32 + u32(moovBody, timescaleAt + 8)
          : u32(moovBody, timescaleAt + 4);
      if (!timescale || !duration) return null;
      const seconds = duration / timescale;
      return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
    }
    if (!boxSize || boxSize < HEADER_BYTES) return null;
    offset += boxSize;
  }
  return null;
};

/** Walks top-level boxes from the start of the file looking for moov. */
const scanForwardForMoov = async file => {
  let offset = 0;
  for (let walked = 0; walked < FORWARD_WALK_LIMIT; walked += 1) {
    if (offset + HEADER_BYTES > file.size) return null;
    const header = await readSlice(file, offset, offset + 16);
    if (header.length < HEADER_BYTES) return null;

    const rawSize = u32(header, 0);
    const boxType = typeAt(header, 4);

    // rawSize 1 => 64-bit size follows the type; 0 => box runs to EOF.
    let boxSize = rawSize;
    let bodyOffset = HEADER_BYTES;
    if (rawSize === 1) {
      if (header.length < 16) return null;
      boxSize = u32(header, 8) * 2 ** 32 + u32(header, 12);
      bodyOffset = 16;
    } else if (rawSize === 0) {
      boxSize = file.size - offset;
    }
    if (boxSize < bodyOffset) return null;

    if (boxType === "moov") {
      const start = offset + bodyOffset;
      const end = Math.min(start + MVHD_SCAN_BYTES, offset + boxSize);
      return readDurationFromMoovBody(await readSlice(file, start, end));
    }
    offset += boxSize;
  }
  return null;
};

/** Searches backwards from EOF for a moov box, in expanding windows. */
const scanTailForMoov = async file => {
  for (const window of TAIL_SCAN_WINDOWS) {
    const start = Math.max(0, file.size - window);
    const buf = await readSlice(file, start, file.size);
    let cursor = buf.length;
    let attempts = 0;
    // Match the 4-char type, then validate the preceding 4 bytes really are a
    // box size that fits the file — guards against the literal bytes "moov"
    // appearing inside compressed media data. Capped so a file full of false
    // matches can't turn this into a quadratic scan.
    while (cursor > 4 && attempts < MAX_TAIL_CANDIDATES) {
      attempts += 1;
      let found = -1;
      for (let i = cursor - 4; i >= 4; i -= 1) {
        if (buf[i] === 0x6d && buf[i + 1] === 0x6f && buf[i + 2] === 0x6f && buf[i + 3] === 0x76) {
          found = i;
          break;
        }
      }
      if (found === -1) break;
      const declaredSize = u32(buf, found - 4);
      if (declaredSize >= HEADER_BYTES && start + found - 4 + declaredSize <= file.size) {
        const body = buf.subarray(found + 4, Math.min(found + 4 + MVHD_SCAN_BYTES, buf.length));
        const seconds = readDurationFromMoovBody(body);
        if (seconds !== null) return seconds;
      }
      cursor = found;
    }
    if (start === 0) break; // already scanned the whole file
  }
  return null;
};

// Top-level box types that legitimately start an ISO-BMFF / QuickTime file.
// Checked before any scanning so a non-ISO container (WebM, Ogg, AVI) bails
// after ONE 12-byte read instead of running the tail scan over tens of MB.
const ISO_BMFF_LEAD_BOXES = ["ftyp", "moov", "mdat", "free", "skip", "wide", "pnot"];

const looksLikeIsoBmff = async file => {
  if (file.size < 12) return false;
  const head = await readSlice(file, 0, 12);
  return head.length >= 8 && ISO_BMFF_LEAD_BOXES.includes(typeAt(head, 4));
};

/**
 * Reads duration straight out of an ISO-BMFF container, bypassing browser
 * decoders.
 *
 * Cost: a 12-byte sniff, then either a handful of 16-byte header reads
 * (faststart layout) or a single 1MB tail read (camera-recorded layout).
 * Non-ISO containers cost only the sniff.
 *
 * @param {File} file
 * @returns {Promise<number|null>} seconds, or null if not parsable.
 */
export const readDurationFromContainer = async file => {
  try {
    if (!file || !file.size) return null;
    if (!(await looksLikeIsoBmff(file))) return null;
    const forward = await scanForwardForMoov(file);
    if (forward !== null) return forward;
    return await scanTailForMoov(file);
  } catch (err) {
    // Malformed file or a failed read — caller falls back to <video>.
    return null;
  }
};

// --- HTMLVideoElement fallback (WebM / Ogg) -------------------------------

/**
 * Reads duration by loading metadata into a detached <video>. Requires codec
 * support, so this is the fallback rather than the primary path.
 *
 * @param {File} file
 * @returns {Promise<number|null>}
 */
export const readDurationFromVideoElement = file =>
  new Promise(resolve => {
    if (!file) {
      resolve(null);
      return;
    }
    let settled = false;
    const url = window.URL.createObjectURL(file);
    const video = document.createElement("video");
    const cleanup = () => {
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("durationchange", onLoaded);
      video.removeEventListener("error", onError);
      video.src = "";
      window.URL.revokeObjectURL(url);
    };
    const finish = value => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      cleanup();
      resolve(value);
    };
    const onLoaded = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) finish(video.duration);
    };
    const onError = () => finish(null);
    const timeoutId = setTimeout(() => finish(null), VIDEO_ELEMENT_TIMEOUT_MS);
    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("durationchange", onLoaded);
    video.addEventListener("error", onError);
    video.preload = "metadata";
    video.src = url;
  });

// --- Public API ------------------------------------------------------------

/**
 * True when a File should be treated as video.
 *
 * Falls back to the extension when the browser reports no MIME type at all:
 * pasted and drag-dropped files routinely arrive with `type === ""`, which
 * previously skipped the duration read entirely.
 *
 * @param {File} file
 * @returns {boolean}
 */
export const isVideoFile = file => {
  const type = (file?.type || "").toLowerCase();
  if (type.includes("video")) return true;
  if (type) return false; // a non-video MIME type is authoritative
  const name = (file?.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  return VIDEO_EXTENSIONS.includes(ext);
};

/**
 * Duration in whole seconds, or null when it genuinely can't be determined.
 * Never throws — callers treat null as "omit the field".
 *
 * @param {File} file
 * @returns {Promise<number|null>}
 */
export const getVideoDurationSeconds = async file => {
  if (!isVideoFile(file)) return null;
  let timeoutId;
  try {
    const read = (async () => {
      const fromContainer = await readDurationFromContainer(file);
      if (fromContainer !== null) return fromContainer;
      return readDurationFromVideoElement(file);
    })();
    // Hard ceiling — see OVERALL_TIMEOUT_MS. Resolving (not rejecting) keeps
    // the "unreadable duration" and "timed out" outcomes identical: null.
    const guard = new Promise(resolve => {
      timeoutId = setTimeout(() => resolve(null), OVERALL_TIMEOUT_MS);
    });
    const seconds = await Promise.race([read, guard]);
    if (!Number.isFinite(seconds) || seconds <= 0) return null;
    return Math.round(seconds);
  } catch (err) {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Builds the spreadable payload fragment for a media object. Use this in every
 * send path so the wire format stays identical across clients:
 *
 *   { ...mediaFields, ...(await buildVideoDurationField(file)) }
 *
 * Returns `{}` for non-videos and for videos whose duration is unreadable —
 * the field is omitted rather than sent as null, matching the mobile client.
 *
 * @param {File} file
 * @returns {Promise<{ videoDurationSeconds?: number }>}
 */
export const buildVideoDurationField = async file => {
  if (!isVideoFile(file)) return {};
  const videoDurationSeconds = await getVideoDurationSeconds(file);
  if (videoDurationSeconds === null) {
    // Loud on purpose: a silent null is how this defect went unnoticed for so
    // long — the MMS still sends, just without the duration.
    // eslint-disable-next-line no-console
    console.warn("[mms] video duration unreadable, field omitted", {
      name: file?.name,
      type: file?.type || "(empty)",
      size: file?.size,
    });
    return {};
  }
  return { videoDurationSeconds };
};
